/** Frosted five-panel bay screen, with a deterministic pressure lift for beat five. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('cull');
  if (!found) throw new Error('plastic sheeting: cull zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`plastic sheeting: cull surface ${String(index)} missing`);
  return colour;
}

/** The panel's rest tilt, alternating in sign so the screen is not five flat planes. */
const REST_ANGLE = 0.03;
/** Where the pivots hang, one millimetre inside the head rail's underside. */
const PIVOT_Y = 2.601;
const PANEL_DEPTH = 0.012;

/**
 * How long a panel has to be to actually reach the floor.
 *
 * **It is not `PIVOT_Y`, and the difference is why this asset failed its own
 * contract.** The panels hang tilted by `REST_ANGLE`, and a tilted panel does
 * not reach as far down as a vertical one: at 0.03 rad a 2.6 m panel falls
 * 1.2 mm short, and its lower back corner swings out another 0.2 mm. The asset
 * therefore floated 1.99 mm above the ground and `asset.spec.ts`'s origin check
 * — which allows one millimetre for tessellation and not two for arithmetic —
 * failed on it. Session 23 shipped this without ever running that gate.
 *
 * Solving for the length that puts the lowest corner exactly on y = 0 keeps the
 * fix where the truth is: the plastic reaches the floor, which is the thing that
 * is physically true about a sheet hanging in a doorway, rather than moving the
 * rail up to hide a gap.
 */
const PANEL_HEIGHT = (PIVOT_Y - (PANEL_DEPTH / 2) * Math.sin(REST_ANGLE)) / Math.cos(REST_ANGLE);

/**
 * Builds the closed screen or the fixed-step-driven pressure lift.
 *
 * The free edge is rotated around its attachment rather than translated. That
 * distinction keeps the plastic's silhouette connected to the rail at every
 * point in the cycle; translating it made the bay look like a sliding door.
 */
export function plasticSheeting(lift: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'plastic-sheeting';
  const pressure = THREE.MathUtils.clamp(lift, 0, 1);
  const railMaterial = surface('metal', zoneSurface(1));
  const frostedMaterials = [
    surface('organic', zoneSurface(3)),
    surface('organic', zoneSurface(4)),
  ] as const;

  const rail = new THREE.Mesh(new THREE.BoxGeometry(2.44, 0.05, 0.06), railMaterial);
  rail.name = 'sheeting-head-rail';
  rail.position.y = 2.625;
  rail.castShadow = true;
  group.add(rail);

  for (let index = 0; index < 5; index += 1) {
    const outerWeight = Math.abs(index - 2) / 2;
    const restAngle = index % 2 === 0 ? REST_ANGLE : -REST_ANGLE;
    // Air is strongest at the loose outer edges. The middle remains more nearly
    // vertical, so a lift reads as pressure leaking through a doorway instead of
    // five mechanically synchronized flaps.
    const liftAngle = 0.30 + outerWeight * 0.25;
    const pivot = new THREE.Group();
    pivot.name = `sheeting-panel-pivot-${index + 1}`;
    pivot.position.set((index - 2) * 0.49, PIVOT_Y, 0);
    pivot.rotation.x = restAngle + pressure * liftAngle;

    const material = frostedMaterials[index % 2];
    if (material === undefined) throw new Error('plastic sheeting: frosted material missing');
    // 0.40 wide on a 0.49 pitch, which leaves a 9 cm slot between every pair.
    //
    // **The slots are the whole point and the first build did not have them.**
    // Five 0.48 m panels with a 1 cm gap is a wall: the bay behind it is the
    // brightest thing in the room and none of it reached the corridor, so the
    // hanging shapes the beat is built around could not be seen at all and the
    // plastic read as a flat dark rectangle (session 23's first frames). Nine
    // centimetres of real gap does what no amount of pale albedo could — the
    // light behind is *actually* visible, and what crosses it is a silhouette.
    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.40, PANEL_HEIGHT, PANEL_DEPTH), material);
    panel.name = `frosted-panel-${index + 1}`;
    panel.position.y = -PANEL_HEIGHT / 2;
    panel.castShadow = true;
    panel.receiveShadow = true;
    pivot.add(panel);
    group.add(pivot);
  }

  return group;
}

export const plasticSheetingAsset: AssetDefinition = {
  id: 'plastic-sheeting',
  label: 'Frosted plastic sheeting',
  description: 'Five-value-frosted PVC panels that lift from their head rail under the plant hiss.',
  footprint: { width: 2.44, height: 2.65, length: 0.17 },
  create: () => plasticSheeting(0),
};
